/**
 * i18n.tsx — minimal, dependency-free localization. A `LocaleProvider` holds the
 * active locale (persisted to localStorage); `useT()` returns a `t(key, vars?)`
 * lookup that falls back to English then to the key itself. The chosen locale also
 * flows to the AI (session.next / interpretation.propose), so generated questions
 * and candidate readings come back in the user's language.
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";

export type Locale = "en" | "de";
export const LOCALES: { id: Locale; label: string; flag: string }[] = [
  { id: "en", label: "English", flag: "EN" },
  { id: "de", label: "Deutsch", flag: "DE" },
];

type Dict = Record<string, string>;

const EN: Dict = {
  "brand": "LINEAGE · EGAE STUDIO",
  // boundary ladder
  "ladder.0": "raw response",
  "ladder.1": "interpreted meaning",
  "ladder.2": "reviewed asset",
  "ladder.3": "approved version",
  "ladder.4": "runtime projection",
  "ladder.5": "permission to act",
  // nav
  "nav.studio": "Studio",
  "nav.evidence": "Evidence",
  "nav.interpret": "Interpret",
  "nav.author": "Author",
  "nav.evaluate": "Evaluate",
  "nav.package": "Package",
  "nav.project": "Project",
  "nav.registry": "Registry",
  "nav.trace": "Trace",
  "nav.commands": "⌘K commands",
  // common
  "common.cancel": "Cancel",
  "common.execute": "Execute",
  "common.run": "Run",
  "common.running": "running…",
  "common.export": "export",
  "common.close": "Close",
  "common.done": "Done",
  "common.loading": "Loading…",
  "common.model": "AI model",
  // home
  "home.kicker": "Evidence-Grounded AI Elicitation · v1.0.0",
  "home.hero": "The interview is an {em}, not a form.",
  "home.hero.em": "instrument",
  "home.lede": "Lineage turns tacit company judgment into {b}. Every instruction an agent reads can be walked back — through projection, approval, review, and interpretation — to the evidence that justified it. Nothing crosses a boundary silently.",
  "home.lede.b": "governed, traceable runtime projections",
  "home.provider.live": "OpenRouter Agents SDK · live",
  "home.provider.synthetic": "set OPENROUTER_API_KEY to go live",
  "count.sessions": "sessions",
  "count.evidence": "evidence",
  "count.interpretations": "interpretations",
  "count.assets": "assets",
  "count.approved": "approved",
  "count.projections": "projections",
  "home.elicit.no": "01 / Elicit",
  "home.elicit.title": "Pick a method pack to begin",
  "home.elicit.desc": "Packs are injectable bundles of instruments matched to a goal. The AI runs an adaptive interview — it reasons out each question. Running a pack produces evidence, never doctrine.",
  "home.pipeline.no": "Pipeline",
  "home.pipeline.title": "Six boundaries no stage may cross",
  "home.registry.link": "primitive registry & governance",
  "pack.primitives": "{n} instruments · status {status}",
  // start session
  "start.title.label": "session title",
  "start.title.ph": "e.g. Refund judgment for the support agent",
  "start.scope.label": "what should the agent learn to handle?",
  "start.scope.ph": "Describe it in your own words — e.g. “How to handle refund requests that fall just outside policy.” No jargon needed.",
  "start.open": "Open session",
  // session
  "session.pack": "Session · {pack}",
  "session.scope": "Goal: {scope}",
  "session.captured": "{n} evidence item(s) captured",
  "session.why": "Why this question",
  "session.thinking": "The AI is reasoning out the next question…",
  "session.next": "Skip · next question",
  "session.done.title": "Enough to synthesize",
  "session.done.desc": "The interviewer judges it has enough evidence. Ask for a candidate reading — it stays a candidate, with counter-evidence attached, until you decide.",
  "session.propose": "Propose candidate interpretation",
  "session.proposing": "synthesizing…",
  "session.close": "Close session",
  "session.viewEvidence": "View evidence graph →",
  "session.q": "Question {n}",
  "session.plan": "Plan",
  "session.plan.covered": "{n}/{m} facets covered",
  "session.showPrompt": "Show the exact prompt the model received",
  // primitive scaffolding
  "prim.construct": "construct target",
  "prim.load": "cognitive load",
  "prim.output": "evidence output",
  "prim.narr.ph": "In your own words…",
  "prim.echo.tag": "reflective-summary · in your words",
  "prim.narr.echo": "So, in your words: ",
  "prim.narr.echo2": " — recorded as evidence, not yet as meaning.",
  "prim.inc.situation": "situation",
  "prim.inc.action": "action",
  "prim.inc.why": "why",
  "prim.inc.situation.ph": "What was the situation?",
  "prim.inc.action.ph": "What did you do?",
  "prim.inc.why.ph": "Why that, and not the alternative?",
  "prim.reflect": "Reflect & record",
  "prim.recordIncident": "Record incident",
  "prim.recordPriority": "Record priority",
  "prim.recordConstruct": "Record construct",
  "prim.recordChain": "Record value chain",
  "prim.ladder.seedFallback": "The stance you just described.",
  "prim.recordPosture": "Record posture",
  "prim.recordDecision": "Record decision",
  "prim.recordSort": "Record sort",
  "prim.recordTrace": "Record decision trace",
  "prim.recordCanvas": "Record canvas",
  // work page heads
  "evidence.title": "Evidence graph",
  "evidence.desc": "Every captured response, stamped with provenance and an explicit boundary on what it does not establish. Raw response ≠ interpreted meaning.",
  "evidence.empty": "No evidence yet. Open a session and answer the AI's questions.",
  "interpret.title": "Candidate interpretations",
  "interpret.desc": "The AI proposes meaning; you own the decision. Only an explicit review can promote a candidate toward an asset. Interpreted meaning ≠ reviewed asset.",
  "interpret.empty": "No candidates yet. From a session, propose a candidate interpretation.",
  "interpret.decision": "your decision",
  "interpret.author": "Author asset from this →",
  "author.title": "Semantic asset versions",
  "author.desc": "Confirmed meaning becomes a reviewable asset. Approving freezes the version — immutable and traceable. Reviewed asset ≠ approved version (approval is gated).",
  "author.empty": "No assets yet. Confirm an interpretation, then author an asset from it.",
  "author.markReviewed": "Mark reviewed",
  "author.approve": "Approve & freeze",
  "author.evaluate": "Evaluate",
  "evaluate.title": "Validity evaluations",
  "evaluate.desc": "Eight dimensions turn alignment into evidence, not assertion. Findings gate projection eligibility. Runtime behavior ≠ proof of validity.",
  "evaluate.empty": "No evaluations yet. Evaluate an asset from the Author page.",
  "package.title": "Alignment doctrine packs",
  "package.desc": "Bundle approved asset versions into a governed pack. Only approved versions are admitted. Approved version ≠ runtime projection.",
  "package.assemble": "Assemble pack",
  "package.approve": "Approve pack",
  "package.project": "Project this pack →",
  "project.title": "Runtime projection & trace",
  "project.desc": "Compile an approved pack into a scoped projection — every block traces to an asset and its evidence. A projection steers behavior but grants no tool permissions. Runtime projection ≠ permission to act.",
  "project.compile": "Compile projection",
  "project.activate": "Activate",
  "project.rollback": "Roll back",
  "project.recordTrace": "Record trace",
  "registry.title": "Method packs & the full primitive index",
  // trace
  "trace.title": "Trace",
  "trace.desc": "The append-only log is the ground truth and the audit trail at once. Narrow it to what matters, follow a thread across the subjects it touches, and open any entry to its full record — tool calls and, where present, reasoning.",
  "trace.search": "Filter the trace…  ↓ into the list · ↑↓ navigate · Enter opens",
  "trace.actor.all": "all",
  "trace.actor.human": "human",
  "trace.actor.agent": "agent",
  "trace.actor.system": "system",
  "trace.thread": "thread",
  "trace.count": "{n} of {total}",
  "trace.empty": "No trace yet. Ask the assistant something, or run a stage of the pipeline.",
  "trace.noMatches": "No entries match this filter.",
  "trace.steps": "tool calls",
  "trace.reasoning": "reasoning trace",
  "trace.payload": "full record",
  "trace.replay": "Replay (edit & rerun)",
  "trace.edit": "edit",
  "trace.editLog": "edit history",
  "trace.humanChanged": "human-changed",
  "trace.turn.human": "human",
  "trace.turn.ai": "AI",
  "trace.turn.prompt": "prompt",
  "trace.turn.response": "response",
  "trace.disclosure": "how the change is disclosed to the model on replay",
  "trace.disclosure.full": "include everything (original, final, intent)",
  "trace.disclosure.final-only": "include only the final value",
  "trace.disclosure.flagged": "final value, flagged as edited",
  "trace.intent.ph": "why you changed it (optional)",
  "trace.edit.save": "Record edit",
  "trace.edit.replay": "Record & replay",
  // palette
  "palette.search": "Search the pipeline…  ↑↓ navigate · ⌥↑↓ groups · Tab → form · Esc closes",
  "palette.empty": "Select a command or query to view its schema and run it.",
  "palette.loading": "Loading commands…",
  "palette.error": "Couldn't load the command surface. Retrying…",
  "palette.retry": "Retry now",
  "palette.noMatches": "No matches",
  "palette.queryEmpty": "No results.",
  "palette.approval": "⚠ Approval required — crossing a boundary",
  "palette.approve": "Approve & execute",
  // console / agent chat
  "console.title": "assistant",
  "console.ask": "✦ ask",
  "console.placeholder": "ask the assistant to explain or do something…",
  "console.send": "send",
  "console.stop": "stop",
  "console.stopping": "stopping…",
  "console.intro": "I can see the workspace and act in it. Ask me to run a stage of the pipeline, explain a boundary, or take you to a page. I propose and operate — only you can approve what crosses a boundary.",
  "console.you": "you",
  "console.clear": "Clear chat",
  // genui scratchpad
  "genui.title": "Scratchpad",
  "genui.open": "Open the scratchpad",
  "genui.intro": "Ask the assistant — it builds a shared, editable scratchpad here instead of chatting. You can edit, reorder, or remove anything it writes, and add notes of your own.",
  "genui.placeholder": "ask the assistant to build, explain, or do something…",
  "genui.working": "working…",
  "genui.error": "the assistant was unavailable",
  "genui.dock": "Dock to the right",
  "genui.float": "Float",
  "genui.collapse": "Collapse",
  "genui.addNote": "Add a note to the scratchpad",
};

const DE: Dict = {
  "brand": "LINEAGE · EGAE STUDIO",
  "ladder.0": "Rohantwort",
  "ladder.1": "interpretierte Bedeutung",
  "ladder.2": "geprüftes Asset",
  "ladder.3": "freigegebene Version",
  "ladder.4": "Laufzeit-Projektion",
  "ladder.5": "Handlungserlaubnis",
  "nav.studio": "Studio",
  "nav.evidence": "Evidenz",
  "nav.interpret": "Deuten",
  "nav.author": "Verfassen",
  "nav.evaluate": "Bewerten",
  "nav.package": "Bündeln",
  "nav.project": "Projizieren",
  "nav.registry": "Register",
  "nav.trace": "Trace",
  "nav.commands": "⌘K Befehle",
  "common.cancel": "Abbrechen",
  "common.execute": "Ausführen",
  "common.run": "Ausführen",
  "common.running": "läuft…",
  "common.export": "exportieren",
  "common.close": "Schließen",
  "common.done": "Fertig",
  "common.loading": "Lädt…",
  "common.model": "KI-Modell",
  "home.kicker": "Evidenzbasierte KI-Elizitation · v1.0.0",
  "home.hero": "Das Interview ist ein {em}, kein Formular.",
  "home.hero.em": "Instrument",
  "home.lede": "Lineage verwandelt implizites Urteilsvermögen in {b}. Jede Anweisung, die ein Agent liest, lässt sich zurückverfolgen — über Projektion, Freigabe, Prüfung und Deutung — bis zur Evidenz, die sie rechtfertigt. Keine Grenze wird stillschweigend überschritten.",
  "home.lede.b": "geregelte, nachvollziehbare Laufzeit-Projektionen",
  "home.provider.live": "OpenRouter Agents SDK · live",
  "home.provider.synthetic": "OPENROUTER_API_KEY setzen, um live zu gehen",
  "count.sessions": "Sitzungen",
  "count.evidence": "Evidenz",
  "count.interpretations": "Deutungen",
  "count.assets": "Assets",
  "count.approved": "freigegeben",
  "count.projections": "Projektionen",
  "home.elicit.no": "01 / Elizitieren",
  "home.elicit.title": "Wählen Sie ein Methodenpaket",
  "home.elicit.desc": "Pakete sind einsetzbare Bündel von Instrumenten, auf ein Ziel abgestimmt. Die KI führt ein adaptives Interview — sie überlegt sich jede Frage. Ein Paket erzeugt Evidenz, niemals Doktrin.",
  "home.pipeline.no": "Pipeline",
  "home.pipeline.title": "Sechs Grenzen, die keine Stufe überschreiten darf",
  "home.registry.link": "Instrumenten-Register & Governance",
  "pack.primitives": "{n} Instrumente · Status {status}",
  "start.title.label": "Titel der Sitzung",
  "start.title.ph": "z. B. Erstattungs-Urteil für den Support-Agenten",
  "start.scope.label": "Was soll der Agent bewältigen lernen?",
  "start.scope.ph": "Beschreiben Sie es in Ihren Worten — z. B. „Wie man Erstattungsanfragen knapp außerhalb der Richtlinie behandelt.“ Kein Fachjargon nötig.",
  "start.open": "Sitzung öffnen",
  "session.pack": "Sitzung · {pack}",
  "session.scope": "Ziel: {scope}",
  "session.captured": "{n} Evidenz-Element(e) erfasst",
  "session.why": "Warum diese Frage",
  "session.thinking": "Die KI überlegt sich die nächste Frage…",
  "session.next": "Überspringen · nächste Frage",
  "session.done.title": "Genug zum Synthetisieren",
  "session.done.desc": "Die Interviewerin hält die Evidenz für ausreichend. Bitten Sie um eine Kandidaten-Deutung — sie bleibt ein Kandidat, mit angehängter Gegenevidenz, bis Sie entscheiden.",
  "session.propose": "Kandidaten-Deutung vorschlagen",
  "session.proposing": "synthetisiere…",
  "session.close": "Sitzung schließen",
  "session.viewEvidence": "Evidenzgraph ansehen →",
  "session.q": "Frage {n}",
  "session.plan": "Plan",
  "session.plan.covered": "{n}/{m} Aspekte abgedeckt",
  "session.showPrompt": "Den exakten Prompt anzeigen, den das Modell erhielt",
  "prim.construct": "Konstruktziel",
  "prim.load": "kognitive Last",
  "prim.output": "Evidenz-Ausgabe",
  "prim.narr.ph": "In Ihren eigenen Worten…",
  "prim.echo.tag": "reflektierende Zusammenfassung · in Ihren Worten",
  "prim.narr.echo": "Also, in Ihren Worten: ",
  "prim.narr.echo2": " — als Evidenz erfasst, noch nicht als Bedeutung.",
  "prim.inc.situation": "Situation",
  "prim.inc.action": "Handlung",
  "prim.inc.why": "warum",
  "prim.inc.situation.ph": "Was war die Situation?",
  "prim.inc.action.ph": "Was haben Sie getan?",
  "prim.inc.why.ph": "Warum so und nicht anders?",
  "prim.reflect": "Spiegeln & erfassen",
  "prim.recordIncident": "Vorfall erfassen",
  "prim.recordPriority": "Priorität erfassen",
  "prim.recordConstruct": "Konstrukt erfassen",
  "prim.recordChain": "Wertekette erfassen",
  "prim.ladder.seedFallback": "Die Haltung, die Sie gerade beschrieben haben.",
  "prim.recordPosture": "Haltung erfassen",
  "prim.recordDecision": "Entscheidung erfassen",
  "prim.recordSort": "Sortierung erfassen",
  "prim.recordTrace": "Entscheidungsspur erfassen",
  "prim.recordCanvas": "Arbeitsfläche erfassen",
  "evidence.title": "Evidenzgraph",
  "evidence.desc": "Jede erfasste Antwort, versehen mit Herkunft und einer expliziten Grenze dessen, was sie nicht belegt. Rohantwort ≠ interpretierte Bedeutung.",
  "evidence.empty": "Noch keine Evidenz. Öffnen Sie eine Sitzung und beantworten Sie die Fragen der KI.",
  "interpret.title": "Kandidaten-Deutungen",
  "interpret.desc": "Die KI schlägt Bedeutung vor; Sie entscheiden. Nur eine ausdrückliche Prüfung kann einen Kandidaten zum Asset befördern. Interpretierte Bedeutung ≠ geprüftes Asset.",
  "interpret.empty": "Noch keine Kandidaten. Schlagen Sie aus einer Sitzung eine Deutung vor.",
  "interpret.decision": "Ihre Entscheidung",
  "interpret.author": "Asset hieraus verfassen →",
  "author.title": "Semantische Asset-Versionen",
  "author.desc": "Bestätigte Bedeutung wird zu einem prüfbaren Asset. Die Freigabe friert die Version ein — unveränderlich und nachvollziehbar. Geprüftes Asset ≠ freigegebene Version (Freigabe ist abgesichert).",
  "author.empty": "Noch keine Assets. Bestätigen Sie eine Deutung und verfassen Sie daraus ein Asset.",
  "author.markReviewed": "Als geprüft markieren",
  "author.approve": "Freigeben & einfrieren",
  "author.evaluate": "Bewerten",
  "evaluate.title": "Validitäts-Bewertungen",
  "evaluate.desc": "Acht Dimensionen machen Ausrichtung zu Evidenz statt Behauptung. Befunde steuern die Projektions-Eignung. Laufzeit-Verhalten ≠ Validitätsnachweis.",
  "evaluate.empty": "Noch keine Bewertungen. Bewerten Sie ein Asset auf der Verfassen-Seite.",
  "package.title": "Ausrichtungs-Doktrinpakete",
  "package.desc": "Bündeln Sie freigegebene Asset-Versionen zu einem geregelten Paket. Nur freigegebene Versionen werden zugelassen. Freigegebene Version ≠ Laufzeit-Projektion.",
  "package.assemble": "Paket zusammenstellen",
  "package.approve": "Paket freigeben",
  "package.project": "Dieses Paket projizieren →",
  "project.title": "Laufzeit-Projektion & Spur",
  "project.desc": "Kompilieren Sie ein freigegebenes Paket zu einer begrenzten Projektion — jeder Block verweist auf ein Asset und seine Evidenz. Eine Projektion steuert Verhalten, gewährt aber keine Werkzeugrechte. Laufzeit-Projektion ≠ Handlungserlaubnis.",
  "project.compile": "Projektion kompilieren",
  "project.activate": "Aktivieren",
  "project.rollback": "Zurückrollen",
  "project.recordTrace": "Spur erfassen",
  "registry.title": "Methodenpakete & das vollständige Instrumenten-Register",
  // trace — der Name lautet in beiden Sprachen gleich „Trace“
  "trace.title": "Trace",
  "trace.desc": "Das append-only-Protokoll ist zugleich Grundwahrheit und Prüfpfad. Engen Sie es auf das Wesentliche ein, folgen Sie einem Faden über die berührten Subjekte und öffnen Sie jeden Eintrag bis zum vollständigen Datensatz — Werkzeugaufrufe und, wo vorhanden, das Reasoning.",
  "trace.search": "Trace filtern…  ↓ in die Liste · ↑↓ navigieren · Enter öffnet",
  "trace.actor.all": "alle",
  "trace.actor.human": "Mensch",
  "trace.actor.agent": "Agent",
  "trace.actor.system": "System",
  "trace.thread": "Faden",
  "trace.count": "{n} von {total}",
  "trace.empty": "Noch keine Spur. Fragen Sie den Assistenten etwas oder führen Sie eine Stufe der Pipeline aus.",
  "trace.noMatches": "Keine Einträge passen zu diesem Filter.",
  "trace.steps": "Werkzeugaufrufe",
  "trace.reasoning": "Reasoning-Spur",
  "trace.payload": "vollständiger Datensatz",
  "trace.replay": "Erneut abspielen (bearbeiten & neu starten)",
  "trace.edit": "bearbeiten",
  "trace.editLog": "Änderungsverlauf",
  "trace.humanChanged": "vom Menschen geändert",
  "trace.turn.human": "Mensch",
  "trace.turn.ai": "KI",
  "trace.turn.prompt": "Eingabe",
  "trace.turn.response": "Antwort",
  "trace.disclosure": "wie die Änderung dem Modell beim Abspielen offengelegt wird",
  "trace.disclosure.full": "alles einschließen (Original, Endfassung, Absicht)",
  "trace.disclosure.final-only": "nur die Endfassung einschließen",
  "trace.disclosure.flagged": "Endfassung, als bearbeitet markiert",
  "trace.intent.ph": "warum Sie es geändert haben (optional)",
  "trace.edit.save": "Änderung erfassen",
  "trace.edit.replay": "Erfassen & abspielen",
  "palette.search": "Pipeline durchsuchen…  ↑↓ navigieren · ⌥↑↓ Gruppen · Tab → Formular · Esc schließt",
  "palette.empty": "Wählen Sie einen Befehl oder eine Abfrage, um das Schema zu sehen und auszuführen.",
  "palette.loading": "Befehle werden geladen…",
  "palette.error": "Befehlsoberfläche konnte nicht geladen werden. Neuer Versuch…",
  "palette.retry": "Jetzt erneut versuchen",
  "palette.noMatches": "Keine Treffer",
  "palette.queryEmpty": "Keine Ergebnisse.",
  "palette.approval": "⚠ Freigabe nötig — eine Grenze wird überschritten",
  "palette.approve": "Freigeben & ausführen",
  "console.title": "Assistent",
  "console.ask": "✦ fragen",
  "console.placeholder": "den Assistenten bitten, etwas zu erklären oder zu tun…",
  "console.send": "senden",
  "console.stop": "stopp",
  "console.stopping": "wird gestoppt…",
  "console.intro": "Ich sehe den Arbeitsbereich und kann darin handeln. Bitten Sie mich, eine Stufe der Pipeline auszuführen, eine Grenze zu erklären oder zu einer Seite zu navigieren. Ich schlage vor und operiere — nur Sie geben frei, was eine Grenze überschreitet.",
  "console.you": "Sie",
  "console.clear": "Chat leeren",
  // genui scratchpad
  "genui.title": "Scratchpad",
  "genui.open": "Scratchpad öffnen",
  "genui.intro": "Fragen Sie den Assistenten — er baut hier ein geteiltes, bearbeitbares Scratchpad auf, statt zu chatten. Sie können alles bearbeiten, umsortieren oder entfernen und eigene Notizen hinzufügen.",
  "genui.placeholder": "den Assistenten bitten, etwas zu bauen, zu erklären oder zu tun…",
  "genui.working": "arbeitet…",
  "genui.error": "der Assistent war nicht erreichbar",
  "genui.dock": "Rechts andocken",
  "genui.float": "Schweben",
  "genui.collapse": "Einklappen",
  "genui.addNote": "Notiz zum Scratchpad hinzufügen",
};

const DICTS: Record<Locale, Dict> = { en: EN, de: DE };

interface Ctx {
  locale: Locale;
  setLocale: (l: Locale) => void;
  t: (key: string, vars?: Record<string, string | number>) => string;
}
const LocaleContext = createContext<Ctx | null>(null);
const KEY = "lineage-locale";

function interpolate(s: string, vars?: Record<string, string | number>): string {
  if (!vars) return s;
  return s.replace(/\{(\w+)\}/g, (_, k) => (k in vars ? String(vars[k]) : `{${k}}`));
}

export function LocaleProvider({ children }: { children: ReactNode }): JSX.Element {
  const [locale, setLocaleState] = useState<Locale>(() => {
    const saved = typeof localStorage !== "undefined" ? localStorage.getItem(KEY) : null;
    return saved === "de" || saved === "en" ? saved : "en";
  });
  useEffect(() => {
    document.documentElement.lang = locale;
  }, [locale]);
  const setLocale = useCallback((l: Locale) => {
    setLocaleState(l);
    try {
      localStorage.setItem(KEY, l);
    } catch {
      /* ignore */
    }
  }, []);
  const t = useCallback(
    (key: string, vars?: Record<string, string | number>) =>
      interpolate(DICTS[locale][key] ?? EN[key] ?? key, vars),
    [locale],
  );
  const value = useMemo(() => ({ locale, setLocale, t }), [locale, setLocale, t]);
  return <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>;
}

export function useI18n(): Ctx {
  const ctx = useContext(LocaleContext);
  if (!ctx) throw new Error("useI18n outside LocaleProvider");
  return ctx;
}
export function useT(): Ctx["t"] {
  return useI18n().t;
}

export function LocaleSwitch(): JSX.Element {
  const { locale, setLocale } = useI18n();
  return (
    <div className="locale-switch">
      {LOCALES.map((l) => (
        <button key={l.id} className={`locale-btn ${locale === l.id ? "on" : ""}`} onClick={() => setLocale(l.id)} title={l.label}>
          {l.flag}
        </button>
      ))}
    </div>
  );
}
